import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useMemo,
  useState
} from 'react';
import { useToggle } from 'usehooks-ts';

export interface LayoutProviderState {
  activeMenu: string;
  navIsCollapsed: boolean;
  isStreetView: boolean;
  toggleNavIsCollapsed: () => void;
  toggleIsStreetView: () => void;
  setActiveMenu: Dispatch<SetStateAction<string>>;
}

const LayoutContext = createContext<LayoutProviderState>({
  navIsCollapsed: true,
  activeMenu: 'Overview',
  isStreetView: true,
  setActiveMenu: () => {},
  toggleNavIsCollapsed: () => {},
  toggleIsStreetView: () => {}
});

/**
 * Creates a layout provider component that manages the state of the layout.
 * @param {PropsWithChildren} props - The properties passed to the component.
 * @returns {ReactElement} The rendered layout provider component.
 */
const LayoutProvider = (props: PropsWithChildren) => {
  const [navIsCollapsed, toggleNavIsCollapsed] = useToggle(false);
  const [isStreetView, toggleIsStreetView] = useToggle(true);
  const [activeMenu, setActiveMenu] = useState('Dashboard');
  const value = useMemo(
    () => ({
      activeMenu,
      navIsCollapsed,
      isStreetView,
      setActiveMenu,
      toggleNavIsCollapsed,
      toggleIsStreetView
    }),
    [activeMenu, isStreetView, navIsCollapsed]
  );

  return (
    <LayoutContext.Provider value={value}>
      {props.children}
    </LayoutContext.Provider>
  );
};

export { LayoutContext, LayoutProvider };
